import type { MatchLogEventType, MatchLogGoalOutcome, MatchLogTimeKind } from "./types";

export type MatchLogTypeVisual = {
  iconColor: string;
  backgroundColor: string;
  borderColor: string;
  /** Klucz ikony SVG (bez MaterialCommunityIcons). */
  icon: "soccer" | "minusCircle" | "swap" | "clock" | "note";
};

const PRIMARY = "#0091FF";
const TURQUOISE = "#40E0D0";
const ORANGE = "#FB9614";
const RED = "#FF3700";
const YELLOW = "#FFCB00";
const WHITE = "#FFFFFF";

export const MATCH_LOG_GOAL_OUTCOME_VISUALS: Record<MatchLogGoalOutcome, MatchLogTypeVisual> = {
  scored: {
    icon: "soccer",
    iconColor: WHITE,
    backgroundColor: PRIMARY,
    borderColor: PRIMARY,
  },
  conceded: {
    icon: "minusCircle",
    iconColor: WHITE,
    backgroundColor: ORANGE,
    borderColor: ORANGE,
  },
};

export const MATCH_LOG_TYPE_VISUALS: Record<MatchLogEventType, MatchLogTypeVisual> = {
  goal: MATCH_LOG_GOAL_OUTCOME_VISUALS.scored,
  yellowCard: {
    icon: "soccer",
    iconColor: YELLOW,
    backgroundColor: "rgba(255, 203, 0, 0.2)",
    borderColor: "rgba(255, 203, 0, 0.5)",
  },
  redCard: {
    icon: "soccer",
    iconColor: RED,
    backgroundColor: "rgba(255, 55, 0, 0.2)",
    borderColor: "rgba(255, 55, 0, 0.5)",
  },
  substitution: {
    icon: "swap",
    iconColor: TURQUOISE,
    backgroundColor: "rgba(64, 224, 208, 0.13)",
    borderColor: "rgba(64, 224, 208, 0.4)",
  },
  time: {
    icon: "clock",
    iconColor: WHITE,
    backgroundColor: "rgba(0, 145, 255, 0.35)",
    borderColor: "rgba(0, 145, 255, 0.5)",
  },
  note: {
    icon: "note",
    iconColor: TURQUOISE,
    backgroundColor: "rgba(64, 224, 208, 0.13)",
    borderColor: "rgba(64, 224, 208, 0.4)",
  },
};

export const getMatchLogTypeVisual = (
  type: MatchLogEventType,
  goalOutcome?: MatchLogGoalOutcome,
): MatchLogTypeVisual => {
  if (type === "goal" && goalOutcome) {
    return MATCH_LOG_GOAL_OUTCOME_VISUALS[goalOutcome];
  }
  return MATCH_LOG_TYPE_VISUALS[type];
};

export type MatchLogTimeKindIconKey =
  | "whistle"
  | "pause"
  | "play"
  | "flag"
  | "timer"
  | "sand"
  | "clockEnd"
  | "bullseye";

export const MATCH_LOG_TIME_KIND_ICONS: Record<MatchLogTimeKind, MatchLogTimeKindIconKey> = {
  kickOff: "whistle",
  firstHalfEnd: "pause",
  secondHalfStart: "play",
  fullTime: "flag",
  extraTimeStart: "timer",
  extraTimeHalfEnd: "sand",
  extraTimeSecondHalfStart: "timer",
  extraTimeEnd: "clockEnd",
  penalties: "bullseye",
};

export const MATCH_LOG_COLORS = {
  primary: PRIMARY,
  turquoise: TURQUOISE,
  orange: ORANGE,
  red: RED,
  yellow: YELLOW,
  white: WHITE,
  muted: "#9CB0C4",
  surface: "rgba(156, 176, 196, 0.11)",
  panelBg: "#003E65",
  modalBg: "#1a2530",
} as const;

/** Kamienie milowe na osi — jak numery faz w konspekcie. */
export const MATCH_LOG_MILESTONE_MARKER = {
  circleSize: 24,
  iconSize: 16,
  circleBg: TURQUOISE,
  iconColor: "#0c202a",
  /** Wspólna szerokość guttera osi (eventy i kamienie milowe). */
  railWidth: 28,
  /** Pionowy oddech wokół okręgu na osi. */
  circleMarginY: 6,
} as const;

export const MATCH_LOG_TIMELINE_AXIS = {
  lineWidth: 2,
  lineColor: "rgba(0, 145, 255, 0.45)",
  eventDotTopOffset: 10,
  eventDotMarginY: 1,
  eventDotSize: 8,
} as const;

/** Środek kropki eventu względem góry wiersza osi. */
export const MATCH_LOG_EVENT_DOT_CENTER_Y =
  MATCH_LOG_TIMELINE_AXIS.eventDotTopOffset +
  MATCH_LOG_TIMELINE_AXIS.eventDotMarginY +
  MATCH_LOG_TIMELINE_AXIS.eventDotSize / 2;
